import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { RouteActionEvent, RouteModalHandle, RouteReportModalState } from '../../route-analysis-report.models';
import { emptyRouteModalState } from '../../route-analysis-report.utils';
import { RouteAnalysisTableComponent } from '../../route-analysis-table.component';

declare const $: (element: HTMLElement) => RouteModalHandle;

@Component({ selector: 'route-report-modal', templateUrl: './route-report-modal.component.html' })
export class RouteReportModalComponent implements AfterViewInit, OnDestroy {
  @Input() modalId = '';
  @Input() title = '';
  @Input() state: RouteReportModalState = emptyRouteModalState();
  @Output() closing = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();
  @Output() rowAction = new EventEmitter<RouteActionEvent>();
  @ViewChild('modalRoot', { static: true }) root!: ElementRef<HTMLElement>;
  @ViewChild(RouteAnalysisTableComponent) table?: RouteAnalysisTableComponent;
  private phase: 'hidden' | 'opening' | 'shown' | 'closing' = 'hidden';
  private pendingClose = false;
  private closingEmitted = false;
  private destroyed = false;
  private returnFocus?: HTMLElement;
  private focusTimer?: ReturnType<typeof setTimeout>;
  private readonly onShow = () => {
    this.phase = 'opening'; this.pendingClose = false; this.closingEmitted = false;
    this.returnFocus = this.state.returnFocus || (document.activeElement instanceof HTMLElement ? document.activeElement : undefined);
    this.zone.run(() => this.table?.reset());
  };
  private readonly onShown = () => {
    this.phase = 'shown';
    if (this.pendingClose || this.destroyed) this.hide();
  };
  private readonly onHide = () => { this.phase = 'closing'; this.emitClosing(); };
  private readonly onHidden = () => {
    this.phase = 'hidden'; this.pendingClose = false;
    if (this.destroyed) { this.dispose(); return; }
    this.zone.run(() => this.closed.emit());
    this.focusTimer = setTimeout(() => {
      if (!this.destroyed && this.returnFocus?.isConnected && !this.returnFocus.matches(':disabled')) this.returnFocus.focus();
      this.focusTimer = undefined;
    }, 0);
  };
  constructor(private readonly zone: NgZone) {}
  ngAfterViewInit(): void {
    const element = this.root.nativeElement;
    element.addEventListener('show.bs.modal', this.onShow);
    element.addEventListener('shown.bs.modal', this.onShown);
    element.addEventListener('hide.bs.modal', this.onHide);
    element.addEventListener('hidden.bs.modal', this.onHidden);
  }
  hide(): void {
    if (this.phase === 'opening') {
      this.pendingClose = true; this.emitClosing();
    } else if (this.phase === 'shown') $(this.root.nativeElement).modal('hide');
  }
  ngOnDestroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.focusTimer !== undefined) clearTimeout(this.focusTimer);
    // Bootstrap must finish hiding before disposal so its own backdrop and scroll lock are cleaned up.
    if (this.phase === 'hidden') this.dispose();
    else this.hide();
  }
  private emitClosing(): void {
    if (this.closingEmitted) return;
    this.closingEmitted = true;
    if (!this.destroyed) this.zone.run(() => this.closing.emit());
  }
  private dispose(): void {
    const element = this.root.nativeElement;
    element.removeEventListener('show.bs.modal', this.onShow);
    element.removeEventListener('shown.bs.modal', this.onShown);
    element.removeEventListener('hide.bs.modal', this.onHide);
    element.removeEventListener('hidden.bs.modal', this.onHidden);
    $(element).modal('dispose');
  }
}
